import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { IconHome, IconLayoutDashboard, IconLogout } from "@tabler/icons-react";
import {
  Avatar,
  AvatarImage,
  AvatarFallback,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@lemonadetraining/web-ui";
import { openDashboard } from "./openDashboard";

/* ------- Types */
interface MenuOption {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  linkTo: string;
}

interface RootState {
  user: {
    user: {
      displayName?: string;
      email?: string;
      photoURL?: string;
    };
  };
}

interface UserAvatarAndPopoverMenuProps {
  onLogout: () => void;
}

/* Account Menu options */
const MENU_OPTIONS: MenuOption[] = [
  {
    label: "Home",
    icon: IconHome,
    linkTo: "/",
  },
  {
    label: "Dashboard",
    icon: IconLayoutDashboard,
    linkTo: "/dashboard",
  },
];

/**
 * User avatar with dropdown menu for logged-in users.
 */
function UserAvatarAndPopoverMenu({ onLogout }: UserAvatarAndPopoverMenuProps) {
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.user.user);

  const goToPage = (page: string) => {
    if (page.startsWith("/dashboard")) {
      openDashboard(page);
      return;
    }
    navigate(page);
  };

  // Get initials for fallback
  const getInitials = (name?: string) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative size-9 rounded-full"
          data-testid="user-avatar-button"
        >
          <Avatar size="default">
            <AvatarImage src={user.photoURL} alt={user.displayName || "User"} />
            <AvatarFallback>{getInitials(user.displayName)}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium leading-none">{user.displayName}</p>
            <p className="text-xs text-muted-foreground leading-none">{user.email}</p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          {MENU_OPTIONS.map((option) => (
            <DropdownMenuItem key={option.label} onClick={() => goToPage(option.linkTo)}>
              <option.icon className="mr-2 size-4" />
              {option.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onLogout} variant="destructive" data-testid="logout-button">
          <IconLogout className="mr-2 size-4" />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default UserAvatarAndPopoverMenu;
